// ABOUTME: Inventory menu tab listing every carrier with its combined picking and bulk stock and a
// ABOUTME: status label when it is below its minimum picking stock or its total safety stock.
import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Text from "../../../../../components/theme/text/Text";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import ShLabelSkeleton from "../../../../../components/theme/skeleton/ShLabelSkeleton";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import AllCarriersCard from "../../../../../components/global/cards/settings/carriers/AllCarriersCard";
import { useGetCarrierStockOverviewQuery } from "../../../../../newapi/carrier/carrierSlice";

const SpCarrierDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpCarrierDetails/SpCarrierDetails"));

const gridOptions = {
    export: false,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

// Safety stock is the stricter signal, so it wins over the picking stock warning
const getStockStatus = (row, t) => {
    if (row.belowSafetyStock) return { variant: "red", text: t("below_safety_stock") };
    if (row.belowPickingStock) return { variant: "orange", text: t("below_picking_stock") };
    return { variant: "green", text: t("stock_ok") };
};

const Carriers = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [carrierIsExtended, setCarrierIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    const [carrierId, setCarrierId] = useState();
    const carrierIdRef = useRef(carrierId);

    const getCarrierDetails = (params) => {
        if (!params) return;

        const newCarrierId = parseInt(params.row.id);
        if (carrierIdRef.current !== newCarrierId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setCarrierId(newCarrierId);
            carrierIdRef.current = newCarrierId;
        }
        setCarrierIsExtended(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
        });

        return () => {
        }
    }, []);

    const numberColumn = (field, headerName) => ({
        field,
        headerName,
        flex: 1,
        sortable: ["minimumPickingStock", "safetyStock"].includes(field),
        renderCell: (cellValues) => {
            if (cellValues.row.skeleton) {
                return <ShTextSkeleton />
            }
            return <Text>{cellValues.row[field] ?? "-"}</Text>;
        }
    });

    const columns = [
        {
            field: "name",
            headerName: t("carrier"),
            flex: 2,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return <Text semibold>{cellValues.row.name}</Text>;
            }
        },
        numberColumn("pickingStock", t("picking_stock")),
        numberColumn("bulkStock", t("bulk_stock")),
        numberColumn("totalStock", t("total_stock")),
        numberColumn("minimumPickingStock", t("min_picking_stock")),
        numberColumn("safetyStock", t("safety_stock")),
        {
            field: "status",
            headerName: t("status"),
            flex: 1,
            sortable: false,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShLabelSkeleton />
                }
                const status = getStockStatus(cellValues.row, t);
                return (
                    <ShLabel size={32} fitted variant={status.variant}>
                        {status.text}
                    </ShLabel>
                );
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton />
                }
                return (
                    <AllCarriersCard onClick={() => getCarrierDetails(cellValues)} skeleton={cellValues.row.skeleton} {...cellValues.row} />
                )
            }
        }
    ];

    return (
        <Box
            sx={{
                height: 1,
                "@media screen and (max-width: 47.9375em)": {
                    paddingTop: 0,
                    paddingX: 0,
                }
            }}
        >
            <ShDataGrid2 key={theme.palette.mode}
                sortModel={{ field: columns[0].field, type: "ASC" }}
                content={useGetCarrierStockOverviewQuery}
                title={t("carriers")}
                gridOptions={gridOptions}
                onRowClick={getCarrierDetails}
                columns={columns}>
            </ShDataGrid2>

            {spLoading &&
                <SpCarrierDetails
                    isExtended={carrierIsExtended}
                    setIsExtended={setCarrierIsExtended}
                    carrierId={carrierId}
                />
            }
        </Box>
    )
};

export default Carriers;
